import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppConstants } from '../../../shared/constants/app-constants';

export type ThemePreference = 'light' | 'dark' | null;

/** null = follow the OS (prefers-color-scheme); 'light'/'dark' = explicit user override. */
@Injectable({ providedIn: 'root' })
export class ThemeService {

  private media = (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

  public theme$ = new BehaviorSubject<ThemePreference>(this.readStored());

  constructor(@Inject('LOCALSTORAGE') private localStorage: any) {
    this.apply(this.theme$.value);
    if (this.media) {
      this.media.addEventListener('change', () => {
        if (this.theme$.value === null) {
          this.theme$.next(null);
        }
      });
    }
  }

  setTheme(theme: ThemePreference): void {
    if (theme === 'dark' || theme === 'light') {
      this.localStorage?.setItem(AppConstants.STORAGE_KEY_THEME, theme);
    } else {
      this.localStorage?.removeItem(AppConstants.STORAGE_KEY_THEME);
    }
    this.apply(theme);
    this.theme$.next(theme);
  }

  toggle(): void {
    this.setTheme(this.effectiveDark() ? 'light' : 'dark');
  }

  effectiveDark(): boolean {
    const theme = this.theme$.value;
    return theme === 'dark' || (theme === null && !!this.media?.matches);
  }

  private apply(theme: ThemePreference): void {
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    if (theme === 'dark' || theme === 'light') {
      root.setAttribute('data-theme', theme);
    } else {
      root.removeAttribute('data-theme');
    }
  }

  private readStored(): ThemePreference {
    const stored = this.localStorage?.getItem(AppConstants.STORAGE_KEY_THEME);
    return stored === 'dark' || stored === 'light' ? stored : null;
  }
}
